import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // O <a> si no usas React Router
import { getApi, postApi } from '../../services/apiService'; 
import urlConfig from '../../data/UrlConfig';
import { useAuth } from '../../context/AuthContext';
import { SparklesIcon, PlusIcon, GiftIcon } from '@heroicons/react/24/outline';

const OracleCreditManager = ({ 
  lang = 'es', 
  onCreditUsed
}) => {
  const { user } = useAuth();
  const [creditData, setCreditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConsuming, setIsConsuming] = useState(false);
  const loginPath = `/login?lang=${lang}` 

  const fetchCredits = async () => {
    try {
      const products = await getApi(urlConfig.user_get_products);
      const found = products.find(p => p.code === 'oracle-reading-credits');
      setCreditData(found);
    } catch (e) {
      console.error("Error fetching credits", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchCredits();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleConsumeCredit = async () => {
    if (!creditData || isConsuming) return;
    
    setIsConsuming(true);
    try {
      await postApi(urlConfig.user_consume_product, { 
        productCode: 'oracle-reading-credits'
      });
      
      await fetchCredits(); 
      if (onCreditUsed) onCreditUsed();
    } catch (e) {
      console.error("Error consuming credit", e);
      alert(lang === 'es' ? "Error al procesar el crédito." : "Error processing credit.");
    } finally {
      setIsConsuming(false);
    }
  };

  if (loading) return null;

  // --- SI NO ESTÁ LOGUEADO: BOTÓN PROMO ---
  if (!user) {
    const guestButtonText = lang === 'en'
      ? "Log in & get 3 free credits"
      : "Inicia sesión y obtén 3 créditos gratis";

    return (
      <div className="flex flex-col items-center">
        <Link
          to={loginPath}
          className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-indigo-600 hover:from-amber-400 hover:to-indigo-500 text-white px-6 py-2.5 rounded-full font-bold transition-all shadow-lg hover:shadow-indigo-500/25 transform hover:-translate-y-0.5"
        >
          <GiftIcon className="w-5 h-5 text-amber-200" />
          <span>{guestButtonText}</span>
        </Link>
      </div>
    );
  }

  // --- SI ESTÁ LOGUEADO ---
  const currentCredits = creditData?.credits || creditData?.quantity || 0; 
  const hasCredits = currentCredits > 0;

  const ui = {
    use: lang === 'en' ? `Use Credit (${currentCredits} left)` : `Usar Crédito (${currentCredits} restantes)`,
    getMore: lang === 'en' ? 'Get More Credits' : 'Obtener más créditos',
    consuming: lang === 'en' ? 'Processing...' : 'Procesando...',
    whatsappMsg: lang === 'en' 
      ? "Hi! I'd like to get more credits for the Oracle readings." 
      : "Hola, me gustaría obtener más créditos para mis lecturas del oráculo.",
    warning: lang === 'en'
      ? "Note: Consuming a credit will clear current data. Download your PDF first."
      : "Nota: Al consumir un crédito se borrarán los datos actuales. Descarga tu PDF primero."
  };

  const whatsappLink = `https://wa.me/5491150254270?text=${encodeURIComponent(ui.whatsappMsg)}`;

  return (
    <div className="flex flex-col items-center gap-3">
      {hasCredits ? (
        <>
          <button
            onClick={handleConsumeCredit}
            disabled={isConsuming}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-full font-bold transition-all shadow-lg disabled:opacity-50"
          >
            {isConsuming ? ui.consuming : (
              <>
                <SparklesIcon className="w-5 h-5" />
                {ui.use}
              </>
            )}
          </button>
          
          <p className="text-xs text-gray-500 text-center max-w-[280px] italic px-2">
            {ui.warning}
          </p>
        </>
      ) : (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-full font-bold transition-all shadow-lg"
        >
          <PlusIcon className="w-5 h-5" />
          {ui.getMore}
        </a>
      )}
    </div>
  );
};

export default OracleCreditManager;